import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { ExpectedDialog } from "./ExpectedDialog";
import { fmtTime, fmtDay } from "./pulse";

// One paragraph, one LLM call, cached for the day. Footer: source line · "Why N? See the math" ·
// Expected tag/button · optional "Open <community> →".
export function SummaryPanel({ tenantId, name, snapshot, canExpected = false, onWhy, onOpen, onExpectedChange }) {
  const [state, setState] = useState({ loading: true, text: "", meta: null, error: "" });
  const [expOpen, setExpOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const load = (force = false) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    api.post(`/tenants/${tenantId}/community-pulse/summary`, force ? { force: true } : {})
      .then((r) => setState({ loading: false, text: r.data.text, meta: r.data, error: "" }))
      .catch((e) => setState({ loading: false, text: "", meta: null, error: e?.response?.data?.detail || "The summary didn't come back — try again." }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenantId, snapshot?.computed_at, snapshot?.expected?.id]);
  const exp = snapshot?.expected;
  const clearExpected = async () => {
    setClearing(true);
    try { await api.delete(`/tenants/${tenantId}/community-pulse/expected/${exp.id}`); toast.success("Expected mark removed"); onExpectedChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
    finally { setClearing(false); }
  };
  return (
    <div className="col-span-full bg-white border border-[#E8E2D9] rounded-2xl px-4 sm:px-5 py-4 mt-0.5 mb-1" data-testid={`pulse-summary-${tenantId}`}>
      {state.loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8C827A]" data-testid="pulse-summary-loading"><Loader2 className="h-4 w-4 animate-spin" /> Reading this month's signals…</div>
      ) : state.error ? (
        <div className="text-sm text-[#B91C1C] flex items-center justify-between gap-3" data-testid="pulse-summary-error">
          <span>{state.error}</span>
          <button type="button" onClick={() => load()} className="text-xs font-bold underline shrink-0">Try again</button>
        </div>
      ) : (
        <p className="m-0 text-[14.5px] leading-[1.55] text-[#1C1917]" data-testid="pulse-summary-text">{state.text}</p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mt-3 text-xs text-[#8C827A]">
        <span className="flex items-center gap-2 flex-wrap">
          {state.meta && <span>from {state.meta.from_conversations?.toLocaleString?.() ?? state.meta.from_conversations} conversations · {fmtTime(snapshot?.computed_at)}</span>}
          {exp && (
            <span data-testid="pulse-expected-tag" className="inline-flex items-center gap-1 rounded-full bg-sand-100 text-[#57534E] px-2.5 py-0.5 font-bold">
              Expected · {exp.reason} · until {fmtDay(exp.until_day)}
              {canExpected && (
                <button type="button" data-testid="pulse-expected-clear" onClick={clearExpected} disabled={clearing} title="Remove the Expected mark" className="ml-0.5 text-[#8C827A] hover:text-[#1C1917]">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          )}
        </span>
        <span className="flex items-center gap-3">
          {onWhy && snapshot?.score != null && (
            <button type="button" data-testid={`pulse-why-${tenantId}`} onClick={onWhy} className="font-bold text-[#1C1917] border-b border-[#D8CEC1] hover:border-ink">
              Why {snapshot?.score}? See the math →
            </button>
          )}
          {canExpected && !exp && snapshot?.band && snapshot.band !== "green" && (
            <button type="button" data-testid={`pulse-expected-btn-${tenantId}`} onClick={() => setExpOpen(true)} className="font-bold text-[#57534E] hover:text-[#1C1917]">
              Expected — a known event caused this
            </button>
          )}
          {onOpen && (
            <button type="button" data-testid={`pulse-open-${tenantId}`} onClick={onOpen} className="font-bold text-[#1C1917] border-b border-[#D8CEC1] hover:border-ink">Open {name} →</button>
          )}
        </span>
      </div>
      <ExpectedDialog open={expOpen} onOpenChange={setExpOpen} tenantId={tenantId} name={name} onDone={() => onExpectedChange?.()} />
    </div>
  );
}
